import { useFormik } from 'formik';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import InputBox from 'src/components/Common/InputBox/InputBox';
import DatePicker from 'react-datepicker';
import calenderIcon from 'src/assets/svgs/calenderIcon.svg';
import IconWithButton from 'src/components/Common/IconWithButton';
import { dateTimeFormate } from 'src/utils/function';
import TextWithIconButton from 'src/components/Common/TextWithIconButton';
import rightIconGreen from 'src/assets/svgs/rightIconGreen.svg';
import rightIcon from 'src/assets/svgs/rightIcon.svg';
import { AddEmployeeHourlyCharge, getEmployeeHourlyCharges } from 'src/services/employee';
import { toast } from 'react-toastify';
import moment from 'moment';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
function AddHourlyCharge({ employDetail, setModalOpen, isOnSuccess }: any) {
    const [dateError, setDateError] = useState<string>('');
    const [calenderOpen, setCalenderOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hourlyCharges, setHourlyCharges] = useState([]);

    const onSubmit = async (values: any) => {
        const date = dateTimeFormate(values.date, 'YYYY-MM-DD HH:mm:ss');
        try {
            setIsSubmitting(true);
            const payload = {
                employeeId: employDetail.employeeId,
                hourlyCharge: values.hourlyCharge,
                date: date,
            };
            const res = await AddEmployeeHourlyCharge(payload);

            if (res.status && res.status === 200) {
                toast.success('Employee hourly charge added');
                setModalOpen(false);
                isOnSuccess();
            }
        } catch (error) {
            console.log('Add hourly charge error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const formik = useFormik({
        initialValues: {
            employeeId: employDetail.employeeId,
            hourlyCharge: '',
            date: moment().toDate(),
        },
        onSubmit,
    });

    const fetchHourlyCharges = async (employeeId: any) => {
        let payload = `?employeeId=${employeeId}&page=1&limit=10`;
        try {
            const res = await getEmployeeHourlyCharges(payload);

            if (res.status === 200) {
                const data = res?.data?.data.hourlyCharges;
                setHourlyCharges(data);
            }
        } catch (error) {
            console.log('Error: ', error);
            setHourlyCharges([]);
        }
    };

    useEffect(() => {
        if (employDetail.employeeId) {
            formik.setFieldValue('employeeId', employDetail.employeeId);
            fetchHourlyCharges(employDetail.employeeId);
        }
    }, [employDetail.employeeId]);

    const handleSubmit = () => {
        formik.handleSubmit();
    };

    const CustomDate = forwardRef((props, ref: any) => {
        const date = formik.values.date === null ? 'Date' : dateTimeFormate(formik.values.date, 'DD MMM, YYYY');
        return (
            <div className="" ref={ref}>
                <IconWithButton title={date} icon={calenderIcon} isMoreClass={'max-lg:py-3 max-lg:gap-2 max-lg:rounded-xl max-lg:w-full'} onclick={() => setCalenderOpen(true)} />
            </div>
        );
    });

    const isFormValid = useMemo(() => {
        const hasAllFields = formik.values.employeeId && formik.values.date && formik.values.hourlyCharge;

        const hasNoErrors = Object.keys(formik.errors).length === 0 && !dateError;

        return hasAllFields && hasNoErrors && !isSubmitting;
    }, [formik.values, formik.errors, dateError, isSubmitting]);

    const GetDate = (row: any) => <span>{dateTimeFormate(row.date, 'DD MMM, YYYY')}</span>;
    const GetHourlyCharge = (row: any) => (
        <div className="flex gap-2">
            {row.hourlyCharge} {row.isActive ? <img src={rightIconGreen} className="text-green-500" alt="righticonn" /> : ''}
        </div>
    );

    return (
        <div className="p-6 min-w-full">
            <div>
                <h1 className=" font-bold">Add employee hourly charge</h1>
                <h3 className=" mt-2">
                    {employDetail.name} {employDetail.surname}{' '}
                </h3>
            </div>
            <div className=" mt-4 flex gap-4 md:flex-row flex-col min-w-full">
                <div className="w-full md:w-1/2">
                    <InputBox
                        name="hourlyCharge"
                        type="text"
                        className="bg-gray10 border-none"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        inputId="hourlyCharge"
                        value={formik.values.hourlyCharge}
                        placeholder="Enter The Hourly Charge"
                        error={formik.errors.hourlyCharge && formik.touched.hourlyCharge ? String(formik.errors.hourlyCharge) : ''}
                    />
                </div>
                <div>
                    <div className="w-full mb-4">
                        <DatePicker
                            selected={formik.values.date ? new Date(formik.values.date) : null}
                            onChange={(date: Date) => {
                                formik.setFieldValue('date', date);
                                setCalenderOpen(false);
                            }}
                            open={calenderOpen}
                            onClickOutside={() => setCalenderOpen(false)}
                            customInput={<CustomDate />}
                            showPopperArrow={false}
                            calendarClassName="sort-custom-datepicker w-[270px]  bg-black text-gray-100"
                            // minDate={toDay.toDate()}
                        ></DatePicker>
                        {formik.touched.date && formik.errors.date && <p className="text-red-500 text-xs">{String(formik.errors.date)}</p>}
                    </div>
                </div>
            </div>
            <TextWithIconButton
                btnClassName={`w-46 h-12 px-6 py-4 rounded-3xl ${!isFormValid ? 'bg-[#D0D7F8] cursor-not-allowed' : 'bg-secondary'} shadow justify-center items-center gap-2 inline-flex`}
                textClassName="text-white text-sm font-bold uppercase"
                imgClassName="w-6 h-6 relative"
                icon={rightIcon}
                textName={'Save'}
                altName="submit"
                onClick={handleSubmit}
                btnType="button"
                disabled={!isFormValid}
            />

            <div className="">
                <p className=" pt-8 pb-2 sm:text-md text-sm font-bold text-gray-500">Employee hourly charge history:</p>
                <DataTable value={hourlyCharges} className=" text-sm">
                    <Column field="hourlyCharge" header="Hourly Charge" body={GetHourlyCharge}></Column>
                    <Column field="date" header="Date" body={GetDate}></Column>
                </DataTable>
            </div>
        </div>
    );
}

export default AddHourlyCharge;
